import styles from "./PortfolioQuote.module.scss";
import React, {useContext, useEffect} from "react";
import {HeaderMenu} from "../../../common/HeaderMenu";
import {useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {rxShowPortfolioTransactionEditor} from "../../../slices/portfolioSlice";
import {quoteDetailsSelector, rxSetQuoteDetails} from "../../../slices/quotesSlice";
import {PortfolioQuoteInfos} from "./PortfolioQuoteInfos";
import useFormat from "../../../util/useFormat";
import clsx from "clsx";
import {PortfolioTransactionEditor} from "../portfolio-transaction-editor/PortfolioTransactionEditor";
import useProject from "../../../util/useProject";
import ColoredAmount from "../../../common/ColoredAmount";
import {
  MODE_CREATE,
  MODE_EDIT,
  PORTFOLIO_TRANSACTION_TYPE_BUY,
  PORTFOLIO_TRANSACTION_TYPE_SELL,
  PORTFOLIO_TRANSACTION_TYPE_SPLIT
} from "../../../codes";
import {FabButton} from "../../../common/FabButton";
import {getQuote} from "../../../api/ApiQuotes";
import useNetwork from "../../../util/useNetwork";
import useRate from "../../../util/useRate";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import {GridComponent, MarkPointComponent, TitleComponent, TooltipComponent} from "echarts/components";
import {BarChart, LineChart} from "echarts/charts";
import {CanvasRenderer} from "echarts/renderers";
import {round} from "../../../util/util";
import {appConfigSelector} from "../../../slices/appSlice";
import {Loader} from "../../../common/Loader";
import {Content, ContentFixed, ContentScroll} from "../../../common/Content";
import {AppContext} from "../../../App";

echarts.use(
  [TitleComponent, TooltipComponent, GridComponent, LineChart, MarkPointComponent, BarChart, CanvasRenderer]
);

const PortfolioQuote = () => {
  const {t} = useTranslation("portfolio");

  const dispatch = useDispatch();
  const {runWithExpiration} = useProject();
  const {handleNetworkError} = useNetwork();
  const {formatDate, formatAmount} = useFormat();
  const {quotes} = useContext(AppContext).portfolio;
  const {quote, loading, error} = useSelector(quoteDetailsSelector);

  const showPortfolioTransactionEditor = (mode, transaction) => dispatch(rxShowPortfolioTransactionEditor({
    mode,
    transaction
  }));

  const params = useParams();

  useEffect(() => {
    getQuote(params.symbol)
      .then(res => dispatch(rxSetQuoteDetails({quote: res, loading: false})))
      .catch(error => handleNetworkError(error, {
        404: () => dispatch(rxSetQuoteDetails({error: 404, loading: false}))
      }));

    return () => {
      dispatch(rxSetQuoteDetails({quote: null, loading: true, error: null}));
    }
  }, [params.symbol, dispatch, handleNetworkError]);

  if (error === 404) {
    return <div className={styles.unknownQuote}>
      <span className="material-icons" style={{fontSize: 50}}>not_listed_location</span>
    </div>;
  } else if (loading) {
    return <div className={styles.loader}>
      <Loader/>
    </div>;
  } else {
    const userQuote = quotes[quote.symbol] || {transactions: []};

    const transactionsByType = [];

    let lastItem = {type: null};

    [...userQuote.transactions, ...(quote.splits || []).map(s => ({...s, type: PORTFOLIO_TRANSACTION_TYPE_SPLIT}))]
      .toSorted((a, b) => b.valueDate.localeCompare(a.valueDate))
      .forEach(t => {
        if (t.type !== lastItem.type || t.type === PORTFOLIO_TRANSACTION_TYPE_SPLIT) {
          lastItem = {type: t.type, items: []};

          if (t.type === PORTFOLIO_TRANSACTION_TYPE_BUY) {
            lastItem.style = styles.typeBuy;
            lastItem.label = "list_type_buy";
          } else if (t.type === PORTFOLIO_TRANSACTION_TYPE_SELL) {
            lastItem.style = styles.typeSell;
            lastItem.label = "list_type_sell";
          } else if (t.type === PORTFOLIO_TRANSACTION_TYPE_SPLIT) {
            lastItem.style = styles.typeSplit;
            lastItem.label = "list_type_split";
          }

          transactionsByType.push(lastItem);
        }

        lastItem.items.push(t);
      });

    const hist = (quote.history || []).toSorted((a, b) => a.valueDate.localeCompare(b.valueDate));
    const dates = hist.map(h => formatDate(h.valueDate));
    const prices = hist.map(h => h.price);

    return <Content>
      <ContentFixed>
        <HeaderMenu title={t("header_title_portfolio_quote", {symbol: quote.symbol, quote: quote.name})}/>

        <PortfolioQuoteInfos quote={quote} userQuote={userQuote}/>
      </ContentFixed>

      <ContentScroll>
        <PortfolioTransactionEditor/>

        <FabButton onClick={runWithExpiration(() => showPortfolioTransactionEditor(MODE_CREATE, {
          symbol: quote.symbol,
          price: round(quote.price, 2)
        }))}/>

        <div className={styles.viewContainer}>
          <div className={styles.timelineBar}/>

          <div className={styles.chart}>
            <ReactEChartsCore
              echarts={echarts}
              option={{
                grid: {
                  top: 20, right: 10, bottom: 50, left: 10
                },
                color: ["#555"],
                animation: false,
                tooltip: {
                  trigger: "axis",
                  axisPointer: {
                    type: "cross"
                  },
                  formatter: params => {
                    return "<div class=\"echart-tooltip\">" +
                      "<p class=\"echart-tooltip-title\">" + params[0].name + "</p>" +
                      "<div>" + formatAmount(params[0].data, quote.currency) + "</div>" +
                      "</div>";
                  }
                },
                xAxis: {
                  type: "category",
                  data: dates,
                  axisLabel: {
                    showMinLabel: false
                  }
                },
                yAxis: {
                  type: "value",
                  min: v => v.min - (v.max - v.min) / 10,
                  max: v => v.max + (v.max - v.min) / 10,
                  splitLine: {
                    lineStyle: {
                      color: "#ddd"
                    }
                  },
                  axisLabel: {
                    formatter: val => formatAmount(val, quote.currency),
                    inside: true
                  }
                },
                series: [
                  {
                    showSymbol: false,
                    data: prices,
                    type: "line",
                    lineStyle: {
                      width: 1
                    },
                    areaStyle: {
                      color: "rgba(0, 0, 0, 0.1)"
                    },
                    markPoint: {
                      symbol: "diamond",
                      symbolSize: 15,
                      data: (userQuote.transactions || []).map(t => {
                        const index = dates.indexOf(formatDate(t.valueDate));

                        return ({
                          coord: [index, prices[index]],
                          itemStyle: {
                            color: t.type === PORTFOLIO_TRANSACTION_TYPE_BUY ? "#30a14e" : "#cc0000"
                          }
                        });
                      })
                    }
                  }
                ]
              }}
              notMerge={true}
              lazyUpdate={true}
              theme={"theme_name"}
            />
          </div>

          {transactionsByType.map((item, i) => {
            return <div className={styles.actionType} key={i}>
              <div className={clsx(styles.type, item.style)}>
                <div>{t(item.label)}</div>
              </div>

              <div className={styles.details}>
                {item.type === PORTFOLIO_TRANSACTION_TYPE_BUY && <TransactionsBuy symbol={quote} item={item}/>}
                {item.type === PORTFOLIO_TRANSACTION_TYPE_SELL && <TransactionsSell symbol={quote} item={item}/>}
                {item.type === PORTFOLIO_TRANSACTION_TYPE_SPLIT && <StockSplit item={item}/>}
              </div>
            </div>;
          })}
        </div>
      </ContentScroll>
    </Content>;
  }
};

const TransactionsBuy = ({symbol, item}) => {
  const {t} = useTranslation("portfolio");

  const {runWithExpiration} = useProject();

  const {formatDate, formatAmount} = useFormat();
  const {getPrice} = useRate();
  const {portfolioCurrency} = useSelector(appConfigSelector);

  const dispatch = useDispatch();

  const showPortfolioTransactionEditor = (mode, transaction) => dispatch(rxShowPortfolioTransactionEditor({
    mode,
    transaction
  }));

  const symbolCurrency = symbol.currency;
  const price = symbol.price;

  return <>
    <div className={clsx(styles.transaction, styles.header)}>
      <div className={styles.transactionDetails}>
        <div className={styles.date}>{t("list_header_date")}</div>
        <div className={styles.buyPrice}>{t("list_header_buy_price")}</div>
        <div className={styles.buyQuantity}>{t("list_header_buy_quantity")}</div>
        <div className={styles.fees}>{t("list_header_buy_fees")}</div>
        <div className={styles.buyValue}>{t("list_header_buy_value")}</div>
        <div className={styles.marketValue}>{t("list_header_market_value")}</div>
        <div className={styles.delta}>{t("list_header_delta")}</div>
        <div className={styles.note}>{t("list_header_note")}</div>
      </div>
    </div>

    {item.items.map(transaction => {
      const marketValue = getPrice(transaction.effectiveQuantity * price, symbolCurrency, portfolioCurrency);
      const initialValue = (transaction.quantity * transaction.price * transaction.rate) + (transaction.fees || 0);
      const delta = (marketValue / initialValue * 100) - 100;

      return <div
        className={styles.transaction}
        key={transaction.id}
        onClick={runWithExpiration(() => showPortfolioTransactionEditor(MODE_EDIT, transaction))}
        role="button"
        tabIndex="0"
      >
        <div className={clsx(styles.transactionDetails, "hover-selectable")}>
          <div className={styles.date}>
            <TransactionValue label={t("list_header_date")} value={formatDate(transaction.valueDate)}/>
          </div>
          <div className={styles.buyPrice}>
            <TransactionValue label={t("list_header_buy_price")}
                              value={formatAmount(transaction.price, symbolCurrency)}/>
          </div>
          <div className={styles.buyQuantity}>
            <TransactionValue label={t("list_header_buy_quantity")} value={transaction.quantity}/>
          </div>
          <div className={styles.fees}>
            <TransactionValue label={t("list_header_buy_fees")}
                              value={transaction.fees === null || transaction.fees === undefined ? "-" : formatAmount(transaction.fees, portfolioCurrency)}/>
          </div>

          <div className={styles.mobileSeparator}/>

          <div className={styles.buyValue}>
            <TransactionValue label={t("list_header_buy_value")} value={formatAmount(initialValue, portfolioCurrency)}/>
          </div>
          <div className={styles.marketValue}>
            <TransactionValue label={t("list_header_market_value")}
                              value={formatAmount(marketValue, portfolioCurrency)}/>
          </div>
          <div className={styles.delta}>
            <TransactionValue label={t("list_header_delta")} value={<ColoredAmount
              amount={delta}>{(delta > 0 ? "+" : "") + delta.toFixed(2) + " %"}</ColoredAmount>}/>
          </div>
          <div className={clsx(styles.note, !transaction.note || transaction.note.length === 0 ? styles.noNote : null)}>
            {transaction.note || "-"}
          </div>
        </div>
      </div>;
    })}
  </>;
};

const TransactionValue = ({label, value}) => {
  return <div className={styles.transactionValue}>
    <div className={styles.valueLabel}>{label}</div>
    <div className={styles.valueValue}>{value}</div>
  </div>;
};

const TransactionsSell = ({symbol, item}) => {
  const {t} = useTranslation("portfolio");

  const {portfolioCurrency} = useSelector(appConfigSelector);
  const {runWithExpiration} = useProject();
  const {formatDate, formatAmount} = useFormat();

  const dispatch = useDispatch();

  const showPortfolioTransactionEditor = (mode, transaction) => dispatch(rxShowPortfolioTransactionEditor({
    mode,
    transaction
  }));

  const symbolCurrency = symbol.currency;

  return <>
    <div className={clsx(styles.transaction, styles.header)}>
      <div className={styles.transactionDetails}>
        <div className={styles.date}>{t("list_header_date")}</div>
        <div className={styles.sellPrice}>{t("list_header_sell_price")}</div>
        <div className={styles.sellQuantity}>{t("list_header_sell_quantity")}</div>
        <div className={styles.fees}>{t("list_header_sell_fees")}</div>
        <div className={styles.sellValue}>{t("list_header_sell_value")}</div>
        <div className={styles.note}>{t("list_header_note")}</div>
      </div>
    </div>

    {item.items.map(transaction => {
      const initialValue = (transaction.quantity * transaction.price * transaction.rate) - (transaction.fees || 0);

      return <div
        className={styles.transaction}
        key={transaction.id}
        onClick={runWithExpiration(() => showPortfolioTransactionEditor(MODE_EDIT, transaction))}
        role="button"
        tabIndex="0"
      >
        <div className={styles.transactionDetails}>
          <div className={styles.date}>
            <TransactionValue label={t("list_header_date")} value={formatDate(transaction.valueDate)}/>
          </div>
          <div className={styles.sellPrice}>
            <TransactionValue label={t("list_header_sell_price")}
                              value={formatAmount(transaction.price, symbolCurrency)}/>
          </div>
          <div className={styles.sellQuantity}>
            <TransactionValue label={t("list_header_sell_quantity")} value={transaction.quantity}/>
          </div>
          <div className={styles.fees}>
            <TransactionValue label={t("list_header_sell_fees")}
                              value={transaction.fees === null || transaction.fees === undefined ? "-" : formatAmount(transaction.fees, portfolioCurrency)}/>
          </div>
          <div className={styles.sellValue}>
            <TransactionValue label={t("list_header_sell_value")}
                              value={formatAmount(initialValue, portfolioCurrency)}/>
          </div>
          <div
            className={clsx(styles.note, !transaction.note || transaction.note.length === 0 ? styles.noNote : null)}>
            {transaction.note || "-"}
          </div>
        </div>
      </div>;
    })}
  </>;
};

const StockSplit = ({item}) => {
  const {formatDate} = useFormat();

  const split = item.items[0];

  return <div className={styles.split}>{formatDate(split.valueDate)} ({split.ratioFrom}:{split.ratioTo})</div>;
};

export default PortfolioQuote;
